import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Search, Loader2, AlertCircle, Check, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { PRIORIDADES } from './utils/constants';

/**
 * Modal para crear una tarea con prefill desde otra entidad de Centro Sura.
 *
 * Props:
 * - isOpen, onClose
 * - tituloPrefill, descripcionPrefill (opcional): valor inicial de los campos
 * - prioridadPrefill, fechaPrefill (opcional)
 * - entidadTipo, entidadId (opcional): si se entregan, al crear la tarea
 *   se vincula automaticamente con esa entidad
 * - contextLabel (opcional): texto pequeño "Desde minuta XYZ" en el header
 * - onCreated(tarea): callback tras crear (recibe la tarea completa)
 * - navigateAfter (default true): si true, navega al slide-over de la tarea
 */
const CrearTareaModal = ({
  isOpen,
  onClose,
  tituloPrefill = '',
  descripcionPrefill = '',
  prioridadPrefill = 'ninguna',
  fechaPrefill = null,
  entidadTipo = null,
  entidadId = null,
  contextLabel = null,
  onCreated,
  navigateAfter = false,
}) => {
  const navigate = useNavigate();

  const [proyectos, setProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(null);
  const [proyectoSearchOpen, setProyectoSearchOpen] = useState(false);
  const [proyectoSearch, setProyectoSearch] = useState('');
  const [loadingProyectos, setLoadingProyectos] = useState(true);
  const proyectoPickerRef = useRef(null);

  const [proyectoDetalle, setProyectoDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState('ninguna');
  const [fecha, setFecha] = useState('');
  const [asignadoId, setAsignadoId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      setTitulo(tituloPrefill.slice(0, 200));
      setDescripcion(descripcionPrefill);
      setPrioridad(prioridadPrefill || 'ninguna');
      setFecha(fechaPrefill || '');
      setAsignadoId(null);
      setProyectoId(null);
      setProyectoDetalle(null);
      setProyectoSearch('');
      setError('');
      setSubmitting(false);
    }
  }, [isOpen, tituloPrefill, descripcionPrefill, prioridadPrefill, fechaPrefill]);

  // Cargar mis proyectos
  useEffect(() => {
    if (!isOpen) return;
    setLoadingProyectos(true);
    axiosInstance.get('/api/proyectos')
      .then(({ data }) => {
        setProyectos(data || []);
        // Auto-seleccionar si hay solo uno
        if (data?.length === 1) setProyectoId(data[0].id);
      })
      .catch((err) => console.error('Error cargando proyectos:', err))
      .finally(() => setLoadingProyectos(false));
  }, [isOpen]);

  // Cargar detalle del proyecto al seleccionar (para miembros)
  useEffect(() => {
    if (!proyectoId) {
      setProyectoDetalle(null);
      setAsignadoId(null);
      return;
    }
    setLoadingDetalle(true);
    axiosInstance.get(`/api/proyectos/${proyectoId}`)
      .then(({ data }) => {
        setProyectoDetalle(data);
        // Default: asignar a mí si soy miembro
        if (data.mi_rol) {
          const yo = data.miembros?.find((m) => m.rol === data.mi_rol);
          if (yo) setAsignadoId(yo.empleado_id);
        }
      })
      .catch((err) => console.error('Error cargando detalle:', err))
      .finally(() => setLoadingDetalle(false));
  }, [proyectoId]);

  // Esc cierra
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Click-outside del picker de proyecto (reemplaza el onBlur+setTimeout que
  // perdía clicks en teclado y al hacer scroll dentro del dropdown).
  useEffect(() => {
    if (!proyectoSearchOpen) return;
    const onClick = (e) => {
      if (proyectoPickerRef.current && !proyectoPickerRef.current.contains(e.target)) {
        setProyectoSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [proyectoSearchOpen]);

  const proyectoSeleccionado = proyectos.find((p) => p.id === proyectoId);

  const proyectosFiltrados = useMemo(() => {
    const q = proyectoSearch.trim().toLowerCase();
    if (!q) return proyectos;
    return proyectos.filter(
      (p) => p.nombre.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
    );
  }, [proyectos, proyectoSearch]);

  const miembros = proyectoDetalle?.miembros || [];

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    setError('');

    const t = titulo.trim();
    if (!t) { setError('Ponle un título a la tarea'); return; }
    if (!proyectoId) { setError('Elige un proyecto destino'); return; }

    setSubmitting(true);
    try {
      const payload = {
        titulo: t,
        descripcion: descripcion.trim() || null,
        prioridad,
      };
      if (fecha) payload.fecha_vencimiento = fecha;
      if (asignadoId) payload.asignados = [asignadoId];

      const { data: nuevaTarea } = await axiosInstance.post(
        `/api/proyectos/${proyectoId}/tareas`,
        payload
      );

      // Vincular con entidad si corresponde
      if (entidadTipo && entidadId) {
        try {
          await axiosInstance.post(`/api/tareas/${nuevaTarea.id}/relaciones`, {
            entidad_tipo: entidadTipo,
            entidad_id: entidadId,
          });
        } catch (linkErr) {
          console.error('Error vinculando:', linkErr);
        }
      }

      onCreated?.(nuevaTarea);

      if (navigateAfter && proyectoSeleccionado?.slug) {
        navigate(`/proyectos/${proyectoSeleccionado.slug}/tareas/${nuevaTarea.numero}`);
      }

      onClose?.();
    } catch (err) {
      console.error('Error creando tarea:', err);
      setError(err.response?.data?.error || 'No pudimos crear la tarea.');
      setSubmitting(false);
    }
  }, [titulo, descripcion, prioridad, fecha, asignadoId, proyectoId, entidadTipo, entidadId, onCreated, navigateAfter, navigate, proyectoSeleccionado, onClose]);

  if (!isOpen) return null;

  const content = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden relative"
        >
          <form onSubmit={handleSubmit}>
            {/* Header (mismo formato simple que NewProjectModal: título + subtítulo + close) */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4">
              <div>
                <h2 className="text-base font-semibold tracking-tight text-slate-900">
                  Crear tarea
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {contextLabel || 'Agrega una tarea a uno de tus proyectos.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md transition-colors disabled:opacity-50"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-6 pb-5 space-y-4">
              {/* Proyecto destino */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Proyecto destino <span className="text-rose-500">*</span>
                </label>
                <div ref={proyectoPickerRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setProyectoSearchOpen((v) => !v)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-md text-left hover:border-slate-400 transition-colors"
                  >
                    {proyectoSeleccionado ? (
                      <>
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: proyectoSeleccionado.color || '#06b6d4' }}
                        />
                        <span className="text-sm text-slate-800 truncate flex-1">
                          {proyectoSeleccionado.nombre}
                        </span>
                        <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                          {proyectoSeleccionado.slug}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-slate-400 flex-1">
                        Elegir proyecto...
                      </span>
                    )}
                  </button>

                  <AnimatePresence>
                    {proyectoSearchOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.1 }}
                        className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 overflow-hidden z-10"
                      >
                        <div className="relative px-2 pt-2">
                          <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                          <input
                            type="text"
                            value={proyectoSearch}
                            onChange={(e) => setProyectoSearch(e.target.value)}
                            placeholder="Buscar proyecto..."
                            className="w-full pl-7 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300"
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto py-1 mt-1">
                          {loadingProyectos ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 size={14} className="text-slate-400 animate-spin" />
                            </div>
                          ) : proyectosFiltrados.length === 0 ? (
                            <div className="px-3 py-3 text-xs text-slate-400 text-center">
                              {proyectoSearch ? 'Sin resultados' : 'Sin proyectos disponibles'}
                            </div>
                          ) : (
                            proyectosFiltrados.map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onMouseDown={() => {
                                  setProyectoId(p.id);
                                  setProyectoSearchOpen(false);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                                  proyectoId === p.id ? 'bg-cyan-50' : 'hover:bg-slate-50'
                                }`}
                              >
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: p.color || '#06b6d4' }}
                                />
                                <span className="text-sm text-slate-800 truncate flex-1">{p.nombre}</span>
                                <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                                  {p.slug}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Titulo */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Título <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  rows={2}
                  maxLength={200}
                  placeholder="¿Qué hay que hacer?"
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors resize-none"
                />
              </div>

              {/* Asignado + Prioridad + Fecha */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Asignado
                  </label>
                  <select
                    value={asignadoId || ''}
                    onChange={(e) => setAsignadoId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    disabled={!proyectoId || loadingDetalle}
                    className="w-full px-2 py-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">Sin asignar</option>
                    {miembros.map((m) => (
                      <option key={m.empleado_id} value={m.empleado_id}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Prioridad
                  </label>
                  <select
                    value={prioridad}
                    onChange={(e) => setPrioridad(e.target.value)}
                    className="w-full px-2 py-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Vencimiento
                  </label>
                  <input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className="w-full px-2 py-2 text-xs bg-white border border-slate-300 rounded-md tabular-nums focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Descripcion */}
              {(descripcion || descripcionPrefill) && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Descripción <span className="text-slate-400">(opcional)</span>
                  </label>
                  <textarea
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors resize-none"
                  />
                </div>
              )}

              {/* Vinculacion */}
              {entidadTipo && (
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-50 px-2 py-1.5 rounded-md">
                  <Check size={11} className="text-emerald-500" />
                  La tarea se vinculará a esta {entidadTipo === 'minuta' ? 'minuta' : entidadTipo}.
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
                  <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-rose-700">{error}</span>
                </div>
              )}
            </div>

            {/* Footer (justify-end consistente con NewProjectModal) */}
            <div className="flex items-center justify-end gap-2 px-6 py-3 bg-slate-50/60 border-t border-slate-200/70">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting || !titulo.trim() || !proyectoId}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={13} className="animate-spin" /> : (navigateAfter && <ExternalLink size={13} />)}
                {submitting ? 'Creando...' : (navigateAfter ? 'Crear y abrir' : 'Crear tarea')}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default CrearTareaModal;
